import { useCallback, useEffect, useRef, useState } from 'react'
import { Shift } from '../types/schedule.model'
import { DRAG_STEP_MINUTES, crossesMidnight } from '../utils/schedule'

export type DragMode = 'move' | 'resize-start' | 'resize-end'

/** A time range being swept out over empty grid, before it becomes a shift. */
export interface RangeSelection {
  date: string
  startMinutes: number
  endMinutes: number
}

interface ShiftSession {
  kind: 'shift'
  shift: Shift
  mode: DragMode
  pointerId: number
  originClientY: number
  originClientX: number
  /** Anchor minute under the pointer when the gesture started */
  anchorMinutes: number
  moved: boolean
  live: Shift
}

interface RangeSession {
  kind: 'range'
  pointerId: number
  originClientY: number
  originClientX: number
  date: string
  anchorMinutes: number
  moved: boolean
  live: RangeSelection
}

type Session = ShiftSession | RangeSession

interface UseShiftDragOptions {
  gridStartMinutes: number
  gridEndMinutes: number
  /** Which day column sits under this horizontal position, if any */
  resolveDateAtX: (clientX: number) => string | null
  /** Minute under this vertical position, using the column's live geometry */
  resolveMinutesAtY: (clientY: number) => number | null
  onCommitShift: (shift: Shift) => void
  onCommitRange: (range: RangeSelection) => void
}

/** Pointer travel before a press counts as a drag rather than a click. */
const DRAG_ACTIVATION_PIXELS = 6

/** How long after a drag a stray click is still ignored. */
const CLICK_SUPPRESSION_MS = 300

const snap = (minutes: number): number =>
  Math.round(minutes / DRAG_STEP_MINUTES) * DRAG_STEP_MINUTES

/**
 * Snapping an EDGE to the slot the pointer is inside, rather than to the
 * nearest step.
 *
 * `Math.round` moves the edge only once the pointer passes the middle of a
 * slot, which reads as lag: you are visibly inside 10:30-11:00 and the
 * selection still ends at 10:30. Flooring the leading edge and ceiling the
 * trailing one makes the highlighted period always cover the slot under the
 * cursor, so it reacts the moment you cross a line.
 */
const floorTo = (minutes: number): number =>
  Math.floor(minutes / DRAG_STEP_MINUTES) * DRAG_STEP_MINUTES

const ceilTo = (minutes: number): number =>
  Math.ceil(minutes / DRAG_STEP_MINUTES) * DRAG_STEP_MINUTES

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max)

/**
 * Pointer-driven move, resize and range-select for the calendar, snapped to
 * half hours.
 *
 * Two things are deliberate here. Listeners live on `window` rather than on the
 * block, because a shift dragged into another day column is re-rendered into a
 * different parent, the original node disappears mid-gesture and would take
 * pointer capture, the drop and the session with it. And every side effect runs
 * in the event handler body rather than inside a setState updater, so React is
 * free to call the updater twice without committing twice.
 */
export const useShiftDrag = (options: UseShiftDragOptions) => {
  const { gridStartMinutes, gridEndMinutes } = options

  const [preview, setPreview] = useState<Shift | null>(null)
  const [range, setRange] = useState<RangeSelection | null>(null)
  const sessionRef = useRef<Session | null>(null)
  // Timestamp rather than a latched boolean: a drag that ends without a click
  // (crossing columns, a cancelled touch) must not arm the suppression forever.
  const suppressClickUntilRef = useRef(0)

  const optionsRef = useRef(options)
  optionsRef.current = options

  const finish = useCallback((commit: boolean) => {
    const session = sessionRef.current
    if (!session) return

    sessionRef.current = null
    setPreview(null)
    setRange(null)

    if (!commit || !session.moved) return

    suppressClickUntilRef.current = performance.now() + CLICK_SUPPRESSION_MS

    if (session.kind === 'shift') {
      const { live, shift } = session
      const changed =
        live.date !== shift.date ||
        live.startMinutes !== shift.startMinutes ||
        live.endMinutes !== shift.endMinutes

      if (changed) optionsRef.current.onCommitShift(live)
      return
    }

    optionsRef.current.onCommitRange(session.live)
  }, [])

  // Window-level listeners for the whole gesture. Bound once and kept for the
  // component's life; they no-op unless a session is open.
  useEffect(() => {
    const handleMove = (event: PointerEvent) => {
      const session = sessionRef.current
      if (!session || session.pointerId !== event.pointerId) return

      const travelled = Math.hypot(
        event.clientX - session.originClientX,
        event.clientY - session.originClientY
      )

      const pointerMinutes = optionsRef.current.resolveMinutesAtY(event.clientY)
      if (pointerMinutes === null) return

      const dayAtPointer =
        session.kind === 'range' || session.mode === 'move'
          ? optionsRef.current.resolveDateAtX(event.clientX)
          : null

      const deltaMinutes = snap(pointerMinutes - session.anchorMinutes)
      const dayChanged = !!dayAtPointer && dayAtPointer !== currentDate(session)

      // Activation needs BOTH real pointer travel and a change worth making,
      // so a shaky click can never re-snap a shift that sits off the half-hour
      // grid, and a slow drag doesn't jump before it has moved a step.
      if (
        !session.moved &&
        (travelled < DRAG_ACTIVATION_PIXELS ||
          (deltaMinutes === 0 && !dayChanged))
      ) {
        return
      }

      session.moved = true

      if (session.kind === 'range') {
        // The anchor slot is always part of the selection, and so is the slot
        // the pointer is in, whichever side of the anchor it ended up on.
        const sweepingDown = pointerMinutes >= session.anchorMinutes
        const start = clamp(
          sweepingDown ? floorTo(session.anchorMinutes) : floorTo(pointerMinutes),
          gridStartMinutes,
          gridEndMinutes - DRAG_STEP_MINUTES
        )
        const end = clamp(
          sweepingDown ? ceilTo(pointerMinutes) : ceilTo(session.anchorMinutes),
          start + DRAG_STEP_MINUTES,
          gridEndMinutes
        )

        session.live = {
          date: dayAtPointer ?? session.date,
          startMinutes: start,
          endMinutes: end
        }
        setRange(session.live)
        return
      }

      const { shift, mode } = session
      const duration = shift.endMinutes - shift.startMinutes

      if (mode === 'move') {
        // Bounds are widened to include the shift's own position, so a shift
        // that already sits outside the window is never yanked into it by an
        // inverted min/max.
        const minStart = Math.min(gridStartMinutes, shift.startMinutes)
        // An overnight shift ends on the NEXT day, so capping its start at
        // `gridEnd - duration` would forbid the very hours it is worked at:
        // a ten-hour night shift could then never start later than 14:00.
        const latestStart = crossesMidnight(shift)
          ? gridEndMinutes - DRAG_STEP_MINUTES
          : gridEndMinutes - duration
        const maxStart = Math.max(minStart, latestStart)
        const start = settle(shift.startMinutes + deltaMinutes, minStart, maxStart)

        session.live = {
          ...shift,
          date: dayAtPointer ?? shift.date,
          startMinutes: start,
          endMinutes: start + duration
        }
      } else if (mode === 'resize-start') {
        // A resize handle IS the edge, so it follows the pointer's slot
        // directly instead of accumulating a rounded delta.
        const maxStart = shift.endMinutes - DRAG_STEP_MINUTES
        const minStart = Math.min(gridStartMinutes, maxStart)
        session.live = {
          ...shift,
          startMinutes: clamp(floorTo(pointerMinutes), minStart, maxStart)
        }
      } else {
        const minEnd = shift.startMinutes + DRAG_STEP_MINUTES
        const maxEnd = Math.max(minEnd, gridEndMinutes)
        session.live = {
          ...shift,
          endMinutes: clamp(ceilTo(pointerMinutes), minEnd, maxEnd)
        }
      }

      setPreview(session.live)
    }

    const handleUp = (event: PointerEvent) => {
      if (sessionRef.current?.pointerId !== event.pointerId) return
      finish(true)
    }

    const handleCancel = (event: PointerEvent) => {
      if (sessionRef.current?.pointerId !== event.pointerId) return
      // A cancelled gesture reverts; committing half a drag is worse than none
      finish(false)
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') finish(false)
    }

    window.addEventListener('pointermove', handleMove)
    window.addEventListener('pointerup', handleUp)
    window.addEventListener('pointercancel', handleCancel)
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleUp)
      window.removeEventListener('pointercancel', handleCancel)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [finish, gridStartMinutes, gridEndMinutes])

  const beginShiftDrag = useCallback(
    (event: React.PointerEvent<HTMLElement>, shift: Shift, mode: DragMode) => {
      // One pointer owns the gesture; a second finger must not hijack it
      if (event.button !== 0 || sessionRef.current) return

      const anchor = optionsRef.current.resolveMinutesAtY(event.clientY)
      if (anchor === null) return

      event.stopPropagation()
      suppressClickUntilRef.current = 0

      sessionRef.current = {
        kind: 'shift',
        shift,
        mode,
        pointerId: event.pointerId,
        originClientX: event.clientX,
        originClientY: event.clientY,
        anchorMinutes: anchor,
        moved: false,
        live: shift
      }
    },
    []
  )

  const beginRangeSelect = useCallback(
    (event: React.PointerEvent<HTMLElement>, date: string) => {
      if (event.button !== 0 || sessionRef.current) return

      const anchorRaw = optionsRef.current.resolveMinutesAtY(event.clientY)
      if (anchorRaw === null) return

      const anchor = clamp(snap(anchorRaw), gridStartMinutes, gridEndMinutes)

      suppressClickUntilRef.current = 0

      sessionRef.current = {
        kind: 'range',
        pointerId: event.pointerId,
        originClientX: event.clientX,
        originClientY: event.clientY,
        date,
        anchorMinutes: anchor,
        moved: false,
        live: {
          date,
          startMinutes: anchor,
          endMinutes: anchor + DRAG_STEP_MINUTES
        }
      }
    },
    [gridStartMinutes, gridEndMinutes]
  )

  /** True for a click that merely ended a drag. Self-expiring, never latched. */
  const isClickFromDrag = useCallback(
    () => performance.now() < suppressClickUntilRef.current,
    []
  )

  return {
    preview,
    range,
    beginShiftDrag,
    beginRangeSelect,
    isClickFromDrag
  }
}

const currentDate = (session: Session): string =>
  session.kind === 'shift' ? session.shift.date : session.date

/**
 * Clamps into range, then re-snaps toward the interior, otherwise a bound
 * that isn't itself on the half-hour grid would leave the shift off-grid.
 */
const settle = (value: number, min: number, max: number): number => {
  const clamped = clamp(value, min, max)
  if (clamped === value) return clamped
  return clamped === max
    ? Math.floor(clamped / DRAG_STEP_MINUTES) * DRAG_STEP_MINUTES
    : Math.ceil(clamped / DRAG_STEP_MINUTES) * DRAG_STEP_MINUTES
}
